import { channel } from 'redux-saga'
import { put, all, take, fork, call, delay, race, select, spawn, debounce } from 'redux-saga/effects'
import * as Store from './store';
import { Dispatch } from 'redux'
import JSZip from 'jszip';

import { StructureSet, dicomToStructureSet, structureSetToDicom, StructureSetModalMessages, duplicateFromArrayBuffer } from '../dicom/structure-set';
import { dicomToImageSlice, Image, ImageSlice } from '../dicom/image';
import { LiveReviewQueryV1, LiveReviewQueryV2 } from './live-review-query';
import { sleep, convertToFileSafe, saveFileOnUserDevice, getFilenameSafeTimestamp, lpad, getDateTimeString } from '../util';
import { ContouringClient } from '../web-apis/contouring-client';
import { ContouringTaskState } from './contouring-task';
import { BatchClient, BatchJobStatus } from '../web-apis/batch-client';
import * as selectors from './selectors';
import { compressGZip, decompressGZip } from './gzip'
import { anonymizeSlice, DicomMapAnonReal } from '../dicom/image_anonymization';
import { rtViewerApiClient } from '../web-apis/rtviewer-api-client';
import { StructureTemplate } from '../web-apis/structure-template';
import { defaultAuths, isDemo } from '../environments';
import { RTViewerWebClient } from '../web-apis/rtviewer-web-client';
import { AppVersionInfo } from './app-version-info';
import { getAppAuthByName, getAppAuthByTier, getBackendClient, getOptionalAuths, getRequiredLiveReviewBackend, getRequiredLiveReviewBackendClient, backends } from '../web-apis/auth';
import { AppAuthState, LogInProcessState } from './auth-state';
import { setLiveReviewIsDownloading, setLiveReviewQueries, setUserSettingPatientInfoVisibility } from './store';
import { Backend } from '../web-apis/backends';
import { LiveReviewClient } from '../web-apis/livereview-client';
import { User } from './user';
import { UserPermissions } from '../web-apis/user-permissions';
import AppAuth, { POPUPS_BLOCKED_ERROR } from '../web-apis/app-auth';
import { AzureDownloadError, LoginError, ApiDownloadError } from './errors';
import { AzureFileInfo, AzureShareInfo } from '../web-apis/azure-files';
import { AzureFileClient } from '../web-apis/azure-file-client';
import { AzureFileRequest, AzureFileShareRequest, LiveReviewV1FileRequest, LiveReviewV2FileRequest } from './requests';
import { ReceivedApiFile, ReceivedAzureFile } from './received-files';
import { getAreConcurrentUploadsEnabled } from '../local-storage';
import { downloadDatasetInfo, downloadDatasetLocks, LockAction, ScanLocks, downloadGradings, DEBOUNCE_GRADINGS_SAVE_MS, GradingToSave } from '../datasets/dataset-files';
import { Dataset } from '../datasets/dataset';
import { DatasetImage } from '../datasets/dataset-image';
import * as datasetFiles from '../datasets/dataset-files';
import WorkState from './work-state';
import { DatasetGradings, StructureSetGrading, convertWorkflowStateToText } from '../datasets/roi-grading';
import { backupGradings } from '../datasets/backups';
import AnnotationQuery from '../components/annotation-page/models/AnnotationQuery';
import { NotificationType, SessionNotification } from '../components/common/models/SessionNotification';
import { ViewerState } from '../rtviewer-core/viewer-state';
import _ from 'lodash';
import { fixMissingMetaheader } from '../dicom/utils';
import { convertUserAccessJsonToViewModels, UserStorageAccountAccess } from '../web-apis/user-access';
import { convertDatasetCategoryJsonIntoViewModels, DatasetPatientCategoryCollection } from '../web-apis/dataset-patient-categories';
import Workspace from './workspace';


const MAX_DOWNLOAD_RETRY_ATTEMPTS = 5;


export const mapDispatchToProps = (dispatch: Dispatch) => ({

    initializeApp() {
        dispatch({ type: Store.initializeApp });
    },

    setCurrentWorkspace(workspace: Workspace) {
        dispatch({ type: Store.setCurrentWorkspace, workspace });
    },

    initializeAnnotationPage() {
        dispatch({ type: Store.initializeAnnotationPage });
    },

    storeFullImage(img: Image) {
        dispatch({ type: Store.receiveFullImageType, image: img });
    },

    storeLocalScan(files: File[], cbReturnId: (scanId: string) => void) {
        if (!files || files.length === 0) {
            return;
        }
        let len = files.length;
        let seriesId = "";
        function storeFile(file: File, arrayBuffer: ArrayBuffer, progress: number, isLastSlice: boolean) {

            dispatch({ type: Store.setLocalFileProgress, progress: progress, total: len });

            try {
                if (file.name.toLowerCase().endsWith('.gz')) { arrayBuffer = decompressGZip(arrayBuffer); }
                let slice: ImageSlice | null = null;

                try {
                    slice = dicomToImageSlice(arrayBuffer);
                }
                catch (err) {
                    // catch the headerless dicom file error from dcmjs and try to fix it
                    // also, regarding the error message: sic
                    if (err.message === 'Invalid DICOM file, expected header is missing') {
                        // try fixing the dicom file manually
                        arrayBuffer = fixMissingMetaheader(arrayBuffer);
                        slice = dicomToImageSlice(arrayBuffer);
                    } else {
                        throw err;
                    }
                }

                if (slice) {
                    seriesId = slice.seriesInstanceUID;
                    dispatch({ type: Store.receiveSliceType, scanId: slice.seriesInstanceUID, arrayBuffer: arrayBuffer, imageSlice: slice, filename: file.name });
                } else {
                    // Allow loading a structure set together with the image slices
                    let ss = dicomToStructureSet(arrayBuffer);
                    if (ss) {
                        const filename = file.name.toLowerCase().endsWith('.gz') ? file.name.slice(0, -3) : file.name;
                        dispatch({ type: Store.storeOriginalStructureSet, structureSetId: ss.structureSetId, file: arrayBuffer, filename: filename });
                        dispatch({ type: Store.receiveStructureSetType, structureSet: ss, filename: file.name });
                    }
                }
                if (isLastSlice) {
                    cbReturnId(seriesId);
                }
            } catch (error) {
                console.error(error);
                const message: string = _.has(error, 'message') ? error.message : error;
                dispatch({ type: Store.fileLoadError, errorMessage: message, fileName: file.name });
            }
        }
        function loopFiles(i: number) {
            let reader = new FileReader();
            reader.onload = function (e) {
                let res: any = reader.result;
                storeFile(files[i], res, i + 1, i === len - 1);
                if (++i < len) {
                    loopFiles(i);
                }
            }
            reader.readAsArrayBuffer(files[i]);
        }

        dispatch({ type: Store.resetLocalFileProgress });

        loopFiles(0);
    },

    unloadScan(scanId: string) {
        dispatch({ type: Store.unloadScanType, scanId: scanId });
    },

    unloadStructureSet(scanId: string, structureSetId: string) {
        dispatch({ type: Store.unloadStructureSetType, scanId: scanId, structureSetId: structureSetId });
    },

    storeStructureSet(arrayBuffer: ArrayBuffer, currentScanId: string, filename: string | null, isAutoContoured: boolean,
        generateNewAzureFileInfo: (seriesId: string, sopId: string) => AzureFileInfo | null,
        cbReturnId: (scanId: string, ssId: string) => void,
        storeOriginalStructureSet = true) {

        if (!arrayBuffer) {
            return;
        }

        let structureSet: StructureSet | null = null;

        try {
            structureSet = dicomToStructureSet(arrayBuffer);
        }
        catch (error) {
            console.error(error);
            const message: string = _.has(error, 'message') ? error.message : error;
            dispatch({ type: Store.fileLoadError, errorMessage: message, fileName: filename || '' });
        }

        if (structureSet) {
            if (structureSet.scanId !== currentScanId) {
                dispatch({ type: Store.fileLoadError, errorMessage: "Structure set belongs to different image. scan: " + currentScanId + ', RTSTRUCT: ' + structureSet.scanId, fileName: filename || '' });
                return;
            }

            let extraOptions: any = {};
            if (filename !== null) {
                extraOptions['filename'] = filename;
            }

            if (isAutoContoured) {
                structureSet.existsInAzure = false;
                structureSet.unsaved = true;
                structureSet.azureFileInfo = generateNewAzureFileInfo(structureSet.seriesUid, structureSet.structureSetId);
            }

            if (storeOriginalStructureSet) {
                // this method should only ever receive uncompressed arraybuffers, so treat the default file extension as so
                const originalFilename = filename || `structureset_${convertToFileSafe(structureSet.label)}.dcm`;
                dispatch({ type: Store.storeOriginalStructureSet, structureSetId: structureSet.structureSetId, file: arrayBuffer, filename: originalFilename });
            }

            dispatch({ type: Store.receiveStructureSetType, arrayBuffer: arrayBuffer, structureSet: structureSet, ...extraOptions });
            cbReturnId(structureSet.scanId, structureSet.structureSetId);
        }
        else {
            dispatch({ type: Store.fileLoadError, errorMessage: "Invalid DICOM structure set", fileName: filename || '' });
        }
    },

    storeLocalStructureSet(file: File, currentScanId: string, cbReturnId: (scanId: string, ssId: string) => void) {
        if (!file) {
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            let arrayBuffer = reader.result as ArrayBuffer;
            if (file.name.toLowerCase().endsWith('.gz')) arrayBuffer = decompressGZip(arrayBuffer)
            this.storeStructureSet(arrayBuffer, currentScanId, file.name, false, () => null, cbReturnId);
        }
        reader.readAsArrayBuffer(file);
    },

    async restoreStructureSetBackup(backupAzureFile: AzureFileInfo, originalStructureSet: StructureSet, cbReturnId: (structureSet: StructureSet) => void, isDuplicating: boolean) {

        if (!originalStructureSet.azureFileInfo) {
            throw new Error('File has no azure entry');
        }

        dispatch({ type: Store.startRestoringBackup });

        try {
            const fileClient = new AzureFileClient(originalStructureSet.azureFileInfo.storageAccountName);
            let arrayBuffer = await fileClient.downloadArrayBuffer(backupAzureFile);
            if (backupAzureFile.filename.toLowerCase().includes('.dcm.gz')) { arrayBuffer = decompressGZip(arrayBuffer); }

            if (isDuplicating) {
                // handle new duplicate creation

                const duplicate = duplicateFromArrayBuffer(arrayBuffer, originalStructureSet);
                if (!duplicate) {
                    throw new Error('Could not duplicate backup structure set');
                }

                duplicate.existsInAzure = false;
                duplicate.unsaved = true;
                duplicate.azureFileInfo = datasetFiles.getStructureSetFileInfo(originalStructureSet.azureFileInfo.getShare(), duplicate.dataset.PatientID, duplicate.frameOfReferenceUid, duplicate.seriesUid, duplicate.structureSetId)

                this.storeNewStructureSet(duplicate);
                cbReturnId(duplicate);
            } else {
                // replace existing work data instead

                const structureSet = dicomToStructureSet(arrayBuffer);
                if (!structureSet) {
                    throw new Error('Backup was an invalid DICOM structure set file');
                }

                structureSet.existsInAzure = true;
                structureSet.unsaved = true;
                structureSet.azureFileInfo = originalStructureSet.azureFileInfo;

                dispatch({ type: Store.receiveStructureSetType, arrayBuffer: arrayBuffer, structureSet: structureSet });
                cbReturnId(structureSet);
            }
        }
        finally {
            dispatch({ type: Store.finishRestoringBackup });
        }
    },

    clearFileLoadErrors() {
        dispatch({ type: Store.clearFileLoadErrors });
    },

    unloadImageAndStructureSets(datasetImage: DatasetImage) {
        dispatch({ type: Store.unloadScanType, scanId: datasetImage.seriesId });
        for (let i = 0; i < datasetImage.structureSets.length; ++i) {
            const ss = datasetImage.structureSets[i];
            dispatch({ type: Store.unloadStructureSetType, scanId: datasetImage.seriesId, structureSetId: ss.sopId });
        }
        dispatch({ type: Store.deleteDownloadType, downloadKey: datasetImage.downloadKey });
    },

    async deleteStructureSet(structureSet: StructureSet, dataset: Dataset | null = null, grading: StructureSetGrading | undefined): Promise<void> {

        // if structure set has been saved to azure, those external files must be removed
        // (if not, it's enough to handle the structure sets internally in-memory)
        if (structureSet.existsInAzure) {
            if (!structureSet.azureFileInfo) {
                throw new Error("azureFileInfo is null");
            }

            const accountName = structureSet.azureFileInfo.storageAccountName;
            const azureFileClient = new AzureFileClient(accountName);
            const path = azureFileClient.getPath(structureSet.azureFileInfo.fileShareName, structureSet.azureFileInfo.path);

            const directoryExists = await azureFileClient.doesDirectoryExist(path);
            if (directoryExists) {
                await azureFileClient.deleteDirectoryRecursive(path);
            }
        }

        dispatch({ type: Store.deleteStructureSetType, structureSet: structureSet });

        // also remove any matching grading
        if (dataset && grading) {
            this.saveGrading(structureSet.structureSetId, null, dataset);
        }
    },

    async uploadStructureSet(structureSet: StructureSet, img: any, dataset: Dataset, grading: StructureSetGrading | undefined) {

        // TODO: not sure why this sleep is here -- investigate if it's necessary
        await sleep(50);

        if (!structureSet.azureFileInfo) {
            throw new Error("azureFileInfo is null");
        }

        const fileInfo = structureSet.azureFileInfo;
        const pathInfo = fileInfo.getPath();
        const useGzip = fileInfo.filename.toLowerCase().endsWith(".gz");

        // username should eventually be retrieved from redux store instead
        const username = rtViewerApiClient.username;
        const azureFileClient = new AzureFileClient(pathInfo, { username });
        const ab = structureSetToDicom(structureSet, img);
        const blob = new Blob([useGzip ? compressGZip(ab) : ab], { type: "" });

        await azureFileClient.createDirIfNotExists(pathInfo);
        await azureFileClient.saveBlob(fileInfo, blob);

        structureSet.unsaved = false;
        structureSet.getRois().forEach(roi => { roi.unsaved = false });
        structureSet.existsInAzure = true;
        structureSet.arrayBuffer = ab;

        // also save any matching grading (this is a forced re-save, we might have renamed ROIs which might not get saved otherwise)
        if (grading) {
            this.saveGrading(structureSet.structureSetId, grading, dataset);
        }
    },

    storeNewStructureSet(structureSet: StructureSet) {
        dispatch({ type: Store.receiveStructureSetType, structureSet: structureSet });
    },

    undoStructureSetChanges(ss: StructureSet): StructureSet | null {
        if (ss.existsInAzure) {
            let ssNew = dicomToStructureSet(ss.arrayBuffer);
            if (ssNew) {
                ssNew.azureFileInfo = ss.azureFileInfo;
                dispatch({ type: Store.receiveStructureSetType, structureSet: ssNew });
                return ssNew;
            }
        }
        else {
            ss.deleted = true;
        }
        dispatch({ type: Store.deleteStructureSetType, structureSet: ss });
        return null;
    },

    async createDownloadTask(datasetImage: DatasetImage) {
        dispatch({ type: Store.receiveDownloadCreated, downloadKey: datasetImage.downloadKey });
    },


    sendFilesForContouring(arrayBuffers: ArrayBuffer[], scanId: string, contouringAction: string, backend: Backend, dicomMapAnonReal: DicomMapAnonReal) {
        dispatch({ type: Store.receiveUploadCreated, scanId: scanId });
        let cnt = 0;
        const sliceCountTotal = arrayBuffers.length;
        arrayBuffers.forEach(ab => {
            const filename = (++cnt) + ".dcm";
            dispatch({ type: Store.receiveUploadAddFile, arrayBuffer: ab, dicomMapAnonReal: dicomMapAnonReal, filename: filename, scanId: scanId, contouringAction: contouringAction, backend: backend, sliceCountTotal: sliceCountTotal });
        });
    },

    clearAllContouringRequests: () => {
        dispatch({ type: Store.unloadAllContouringTasksType });
    },

    seenStructureSet: (structureSetId: string) => {
        dispatch({ type: Store.seenStructureSetType, structureSetId: structureSetId });
    },

    setContouringTaskState: (scanId: string, newContouringState: ContouringTaskState, errorMessage: string) => {
        dispatch({ type: Store.updateContouringTaskType, scanId, newContouringState, errorMessage });
    },

    dismissContouringTask: (scanId: string) => {
        dispatch({ type: Store.dismissContouringTaskType, scanId });
    },

    setBatchJobPaneVisibility(value: boolean) {
        dispatch({ type: Store.setBatchJobPaneVisibilityType, value });
    },

    setUserSettingsDialogVisibility(value: boolean) {
        dispatch({ type: Store.setUserSettingsDialogVisibilityType, value });
    },

    setHelpDialogVisibility(value: boolean) {
        dispatch({ type: Store.setHelpDialogVisibility, value });
    },

    setUserSettingPatientInfoVisibility(showPatientInfo: boolean) {
        dispatch({ type: setUserSettingPatientInfoVisibility, showPatientInfo });
    },

    setUserSettingBackend(backend: Backend | null) {
        dispatch({ type: Store.setUserSettingBackend, backend });

        // set the new backend immediately as being required so UI will prompt user to log
        // into it if necessary
        if (backend !== null) {
            const appAuth = getAppAuthByTier(backend.tier);
            dispatch({ type: Store.setAuthStateAsRequired, appAuthName: appAuth.appName });
        }
    },

    startWatchingBatchJobs: () => {
        dispatch({ type: Store.receiveStartWatchingBatchJobsType });
    },

    startBatchJobRequest: () => {
        dispatch({ type: Store.batchJobRequestLockType, lock: true });
    },

    finishBatchJobRequest: (wasSuccessful: boolean) => {
        if (wasSuccessful) {
            dispatch({ type: Store.batchJobRequestSuccessType });
        } else {
            dispatch({ type: Store.batchJobRequestFailureType });
        }
    },

    addNotification: (notification: SessionNotification, delayInMilliseconds?: number) => {
        // they delay can be used to e.g. wait for a modal dialog to fade out before showing the notification
        const delay = delayInMilliseconds ? delayInMilliseconds : 0;
        setTimeout(() => dispatch({ type: Store.addNotificationType, notification }), delay);
    },

    dismissNotification: (notificationId: string) => {
        dispatch({ type: Store.removeNotificationType, notificationId });
    },

    loadStructureTemplates: () => {
        dispatch({ type: Store.receiveStartFetchingStructureTemplatesType });
    },

    snoozeNewVersionAlert: () => {
        dispatch({ type: Store.snoozeNewVersionAlert });
    },

    setLiveReviewQueries: (urlSearchParams: URLSearchParams): boolean => {
        let queryV1: LiveReviewQueryV1 | null = new LiveReviewQueryV1(urlSearchParams);
        if (!queryV1.isValid()) { queryV1 = null; }
        let queryV2: LiveReviewQueryV2 | null = new LiveReviewQueryV2(urlSearchParams);
        if (!queryV2.isValid()) { queryV2 = null; }

        dispatch({ type: setLiveReviewQueries, queryV1, queryV2 });

        // returns true if any query was valid, false otherwise
        return !!queryV1 || !!queryV2;
    },

    logIntoAppAuth: (appAuth: AppAuth) => {
        dispatch({ type: Store.setAuthStateAsRequired, appAuthName: appAuth.appName });
    },

    requestLogOut: () => {
        dispatch({ type: Store.requestLogOut });
    },

    /**
     * Downloads a Dataset from Azure and stores it into Redux Store. Any existing store entry with the same dataset ID
     * will be replaced.
     * @param azureShare Azure location of the dataset.
     * @param reloadMetaFiles If true, metadata files (gradings, allowed roi names, editor info) are loaded and replaced. 
     * If false, metadata file entries in the dataset are left as null.
     * @param datasetId ID which will be given to the downloaded dataset. This usually matches the azure share location (but it doesn't have to).
     */
    downloadDataset: (azureShare: AzureShareInfo, reloadMetaFiles: boolean, datasetId: string) => {
        dispatch({ type: Store.requestDownloadDataset, azureShare, reloadMetaFiles, datasetId });
    },

    lockDatasetImage: async (datasetImage: DatasetImage, dataset: Dataset, lockAction: LockAction) => {
        dispatch({ type: Store.requestDatasetLock, datasetImage, dataset, lockAction });
    },

    downloadDatasetImage(dataset: Dataset, datasetImage: DatasetImage) {
        this.createDownloadTask(datasetImage);

        // Reload the dataset to ensure that will get all structure sets
        const reloadDataset = true;
        dispatch({ type: Store.requestImageAndStructureSetDownload, datasetImage, dataset, reloadDataset });
    },

    /** Sets an entirely new work state as current work state. */
    setCurrentWorkState: (workState: WorkState | null) => {
        const dispatchedWorkState = workState !== null ? workState : new WorkState(null, null, false, false)
        dispatch({ type: Store.setCurrentWorkState, workState: dispatchedWorkState });

        // clear modified dataset gradings from memory if we're resetting current work state
        if (workState === null) {
            dispatch({ type: Store.clearModifiedDatasetGradings });
        }
    },

    /** Updates selected properties only of the current work state. */
    updateCurrentWorkState: (updatedWorkStateProps: Partial<WorkState>) => {
        dispatch({ type: Store.updateCurrentWorkState, ...updatedWorkStateProps });
    },

    /**
     * Synchronizes a structure set's matching grading sheet, if any, to match
     * the current ROI set in the structure set.
     */
    syncStructureSetGrading: (structureSet: StructureSet, dataset: Dataset) => {
        dispatch({ type: Store.syncStructureSetGrading, structureSet, dataset });
    },

    /**
     * Sets a specific structure set grading sheet into local redux store WITHOUT saving the changes into
     * azure. The redux store will then be out of sync with the data in azure. This function should only 
     * be used for temporary operations that will be reverted or finalized immediately after.
     */
    setGradingWithoutSaving: (structureSetId: string, ssGrading: StructureSetGrading | null, dataset: Dataset) => {
        const gradingsToSave: datasetFiles.GradingToSave[] = [{ ssId: structureSetId, ssGrading }];
        dispatch({ type: Store.setStructureSetGrading, gradingsToSave, dataset });
    },


    /**
     * Saves selected structure set grading sheet, new or updated, to disk (to azure).
     * Set ssGrading to null to remove grading for this structure set completely.
     */
    saveGrading: (structureSetId: string, ssGrading: StructureSetGrading | null, dataset: Dataset, lastUpdated?: string | undefined) => {
        // clone the grading to get rid of its potential Immerable immutability
        const clonedGrading = ssGrading ? _.cloneDeep(ssGrading) : null;
        if (clonedGrading) {
            // update lastUpdated field
            clonedGrading.lastUpdated = lastUpdated ? lastUpdated : getDateTimeString();
        }

        dispatch({ type: Store.saveStructureSetGrading, structureSetId, ssGrading: clonedGrading, dataset });
    },

    /**
     * Saves all supplied dataset gradings to matching grading sheet file on disk (azure).
     * This needs to be called directly in only very rare cases, usually SaveGrading will suffice.
     */
    saveDatasetGradings: (gradingsToSave: datasetFiles.GradingToSave[], dataset: Dataset) => {
        dispatch({ type: Store.saveDatasetGradings, gradingsToSave, dataset });
    },

    /**
     * Reloads dataset gradings for the specified dataset. It's important to do this after a context change
     * (such as changing the scan that's currently active) as saving grading sheets for an individual image
     * may leave the rest of the dataset gradings out of sync.
     */
    reloadDatasetGradings: (dataset: Dataset) => {
        dispatch({ type: Store.reloadDatasetGradings, dataset: dataset });
    },

    /**
     * Load a work state from an annotation query item.
     */
    loadAnnotationQuery: (annotationQuery: AnnotationQuery) => {
        dispatch({ type: Store.loadAnnotationQuery, annotationQuery });
    },

    /**
     * Convert a structure set into DICOM and export it to a backend API.
     */
    async exportStructureSet(structureSet: StructureSet, img: Image, grading: StructureSetGrading | undefined, vs: ViewerState, task: datasetFiles.ExportTask | null, backend: Backend,
        originalDicomFile?: { file: ArrayBuffer, filename: string }, buildId?: string) {
        structureSet.modalMessage = StructureSetModalMessages.Exporting;
        vs.notifyListeners();

        await sleep(500); // This must be really long sleep, otherwise the modal dialog shows as almost transparent (slow fade-in animation)

        try {
            const useOriginalDicom = originalDicomFile !== undefined;

            if (!structureSet.azureFileInfo && !useOriginalDicom) {
                throw new Error("No source file specified for given structure set");
            }

            const useGzip = useOriginalDicom ? false : structureSet.azureFileInfo!.filename.toLowerCase().endsWith(".gz");

            // write workflow state (empty, approved, unapproved) into the DICOM's StructureSetDescription field
            // (we shouldn't use the actual ApprovalStatus field for this)
            const postOps = (dataset: any) => {
                if (grading) {
                    dataset.StructureSetDescription = convertWorkflowStateToText(grading.workflowState);
                }
            };

            const ab = useOriginalDicom ? originalDicomFile!.file : structureSetToDicom(structureSet, img, buildId, postOps);
            const dicomBlob = new Blob([useGzip ? compressGZip(ab) : ab], { type: "" });
            const dicomFilename = useOriginalDicom ? originalDicomFile!.filename : `${structureSet.dataset.StructureSetLabel || structureSet.structureSetId}${useGzip ? '.dcm.gz' : '.dcm'}`

            const defaultTask: datasetFiles.ExportTask = { 'app_id': 'n/a', 'app_name': 'n/a', 'client_id': 'n/a' };
            const taskBlob = new Blob([JSON.stringify(task !== null ? task : defaultTask)], { type: 'application/json' });
            const taskFilename = 'task';

            const liveReviewClient = new LiveReviewClient(getBackendClient(backend));
            const wasSuccessful = await liveReviewClient.exportStructureSet(dicomBlob, dicomFilename, taskBlob, taskFilename);

            let notification: SessionNotification;
            if (wasSuccessful) {
                notification = new SessionNotification(`export-ok-${Date.now().toString()}`, `Structure set ${structureSet.dataset.StructureSetLabel || ''} was exported successfully!`, NotificationType.Success);
            } else {
                notification = new SessionNotification(`export-failed-${Date.now().toString()}`, `Something went wrong with export of structure set ${structureSet.dataset.StructureSetLabel || ''}`, NotificationType.Failure);
            }
            dispatch({ type: Store.addNotificationType, notification });
        }
        finally {
            structureSet.modalMessage = null;
            vs.notifyListeners();
        }
    },

    requestSaveScanToUserDisk(image: Image, anonymizeScan: boolean) {
        const scanId = image.scanId;
        const patientId = image.dicomTags['PatientID'];
        const seriesDescription = image.seriesDescription;
        const zipFilename = anonymizeScan ? `scan-${getFilenameSafeTimestamp()}.zip` : `${convertToFileSafe(`${patientId ? `${patientId}${seriesDescription ? '-' : ''}` : ''}${seriesDescription ? `${seriesDescription.substring(0, 35)}` : ''}`)}.zip`;
        dispatch({ type: Store.requestSaveScanToUserDisk, scanId, zipFilename, anonymizeScan });
    },

    dismissOrientationWarning() {
        dispatch({ type: Store.dismissOrientationWarning });
    },

    resetOrientationWarning() {
        dispatch({ type: Store.resetOrientationWarning });
    },

});

// this is here to keep our redux-store and non-redux store users in sync
function* watchUserUpdate() {
    while (true) {
        // update the rtviewer api client user whenever the user in the redux store is updated
        yield take([Store.setUserDetails, Store.setUserPermissions, Store.setUserSettingBackend]);
        const user: User = yield select(selectors.getUser());
        rtViewerApiClient.updateUser(user);
    }
}

function* watchCurrentDatasetLock() {
    const datasetLockCheckDelayTimeInMs = 5 * 60 * 1000;
    while (true) {
        yield race([delay(datasetLockCheckDelayTimeInMs), take(Store.updateCurrentWorkState), take(Store.setCurrentWorkState)]);
        const currentWorkState = (yield select(selectors.getCurrentWorkState())) as WorkState | undefined;
        if (currentWorkState && currentWorkState.dataset && currentWorkState.dataset.datasetFile) {
            // re-fetch locks
            const locks = yield call(downloadDatasetLocks, currentWorkState.dataset.datasetFile.getShare());
            if (locks !== null) {
                yield put({ type: Store.setDatasetLock, datasetId: currentWorkState.dataset.getDatasetId(), scanLocks: locks, clearRequests: false });
            }
        }

    }
}

function* watchVersion() {
    yield delay(1000);
    while (true) {
        const appVersionInfo: AppVersionInfo = yield RTViewerWebClient.getAppVersionInfo();
        yield put({ type: Store.receiveAppVersionInfo, appVersionInfo });

        // wait for 1 hour before doing another version check
        yield delay(1 * 60 * 60 * 1000);
    }
}

function* watchStructureTemplates() {
    while (true) {
        yield take(Store.receiveStartFetchingStructureTemplatesType);

        const structureTemplates: StructureTemplate[] = yield rtViewerApiClient.getStructureTemplates();
        yield put({ type: Store.setStructureTemplatesType, structureTemplates });

        // the fetch can technically be forced to trigger again by calling 
        // Store.receiveStartFetchingStructureTemplatesType again -- current 
        // implementation only ever does it once, however
    }
}

function* watchBatchJobRequests() {
    while (true) {
        yield take(Store.batchJobRequestLockType);
        yield race({
            success: take(Store.batchJobRequestSuccessType),
            failure: take(Store.batchJobRequestFailureType),
        });
        yield put({ type: Store.batchJobRequestLockType, lock: false });
    }
}

function* watchBatchJobs() {
    yield take(Store.receiveStartWatchingBatchJobsType);
    while (true) {
        const batchJobs: BatchJobStatus[] = yield BatchClient.getBatchJobs();
        yield put({ type: Store.receiveBatchJobsStatusType, batchJobs });

        // either wait for 30 seconds or wait for a signal to immediately do a backend refresh
        yield race({
            interval: delay(30 * 1000),
            take: take(Store.receiveStartWatchingBatchJobsType),
        });
    }
}

export function* downloadDicomAsync(action: any) {
    // const actions: any = [];
    const request = action.request as AzureFileShareRequest;

    const fileInfo = request.fileInfo;
    const isGZip: boolean = fileInfo.filename.toLowerCase().endsWith(".gz");
    const downloadKey = action.request.downloadKey;

    const azureClient = new AzureFileClient(fileInfo);

    let file: ArrayBuffer | null = null;
    let downloadAttempt = 0;
    let downloadSucceeded = false;
    let azureDownloadError: AzureDownloadError | null = null;

    // download the dicom file, retry a couple of times in case of minor network glitches with azure
    while (!downloadSucceeded && downloadAttempt < MAX_DOWNLOAD_RETRY_ATTEMPTS) {
        downloadAttempt++;

        try {
            file = yield call(() => azureClient.downloadArrayBuffer(fileInfo));
        }
        catch (error) {
            console.error(`An error occurred when trying to download DICOM file ${fileInfo.toString()}`);
            console.log(error);
            azureDownloadError = new AzureDownloadError(fileInfo, downloadKey, error);
            continue;
        }

        downloadSucceeded = true;
    }

    if (!downloadSucceeded && azureDownloadError !== null) {
        yield put({ type: Store.receiveDownloadErrorType, error: azureDownloadError });
        return;
    }

    if (file === null || !downloadSucceeded) {
        throw new Error(`An unspecified error occurred when trying to download ${fileInfo.toString()}`);
    }

    if (isGZip) {
        try {
            file = decompressGZip(file);
        }
        catch (error) {
            console.error(error);
            console.log(`Error when trying to decompress ${fileInfo.toString()} -- will try to read it as an uncompressed DICOM file despite the file extension.`);
        }
    }

    try {

        // try to convert DICOM file into either an image slice or a structure set

        const imageSlice = dicomToImageSlice(file);
        if (imageSlice) {
            yield put({ type: Store.receiveSliceType, scanId: imageSlice.seriesInstanceUID, arrayBuffer: file, imageSlice: imageSlice, filename: fileInfo.filename });
            yield put({ type: Store.receiveFileDownloadedType, receivedFile: new ReceivedAzureFile(fileInfo, downloadKey, imageSlice.seriesInstanceUID) });
        }
        else {
            const structureSet = dicomToStructureSet(file);
            if (structureSet) {
                structureSet.azureFileInfo = fileInfo;
                const filename = fileInfo.filename.toLowerCase().endsWith('.gz') ? fileInfo.filename.slice(0, -3) : fileInfo.filename;
                yield put({ type: Store.storeOriginalStructureSet, structureSetId: structureSet.structureSetId, file: file, filename: filename });
                yield put({ type: Store.receiveStructureSetType, structureSet: structureSet });
                yield put({ type: Store.receiveFileDownloadedType, receivedFile: new ReceivedAzureFile(fileInfo, downloadKey, structureSet.scanId, structureSet.structureSetId) });
            }
            else {
                const azureDownloadError = new AzureDownloadError(fileInfo, downloadKey, "Invalid or unsupported DICOM file");
                yield put({ type: Store.receiveDownloadErrorType, error: azureDownloadError });
            }
        }
    }
    catch (error) {
        console.error(error);
        console.log(`Invalid dicom file (${fileInfo.toString()})`);
    }
}


export function* downloadLiveReviewV2DicomFromMVBackendAsync(action: any) {
    const actions: any = [];
    const request = action.request as LiveReviewV2FileRequest;
    const downloadKey = request.downloadKey;
    const path = request.path;
    const liveReviewQuery = request.query;

    const liveReviewBackendClient = getRequiredLiveReviewBackendClient(liveReviewQuery);
    const liveReviewClient = new LiveReviewClient(liveReviewBackendClient);
    let arrayBuffer: ArrayBuffer | null = yield call([liveReviewClient, 'getFile'], liveReviewQuery, path);
    const filename = path.substring(path.lastIndexOf('/') + 1);

    if (arrayBuffer === null) {
        yield put({ type: Store.receiveDownloadErrorType, error: new ApiDownloadError(path, downloadKey, "Cannot download " + path) });
        return;
    }

    if (path.toLowerCase().endsWith(".gz")) {
        try {
            arrayBuffer = decompressGZip(arrayBuffer);
        }
        catch (error) {
            console.log("Error in decompression")
            yield put({ type: Store.receiveDownloadErrorType, error: new ApiDownloadError(path, downloadKey, "Cannot decompress " + path) });
            return;
        }
    }

    let imageSlice = dicomToImageSlice(arrayBuffer);
    if (imageSlice) {
        actions.push({ type: Store.receiveSliceType, scanId: imageSlice.seriesInstanceUID, arrayBuffer: arrayBuffer, imageSlice: imageSlice, filename: filename });
        actions.push({ type: Store.receiveFileDownloadedType, receivedFile: new ReceivedApiFile(path, downloadKey, imageSlice.seriesInstanceUID) });
    }
    else {
        let ss = dicomToStructureSet(arrayBuffer);
        if (ss) {
            ss.azureFileInfo = null;
            const filename = `structureset_${convertToFileSafe(ss.label)}.dcm`;
            actions.push({ type: Store.storeOriginalStructureSet, structureSetId: ss.structureSetId, file: arrayBuffer, filename: filename });
            actions.push({ type: Store.receiveStructureSetType, structureSet: ss });
            actions.push({ type: Store.receiveFileDownloadedType, receivedFile: new ReceivedApiFile(path, downloadKey, ss.scanId, ss.structureSetId) });
        }
        else {
            actions.push({ type: Store.receiveDownloadErrorType, error: new ApiDownloadError(path, downloadKey, "Invalid DICOM file") });
        }
    }
    for (let i = 0; i < actions.length; ++i) {
        yield put(actions[i]);
    }
}

export function* uploadDicomAsync(action: any) {
    const backend = action.backend as Backend;
    const client = new ContouringClient(getBackendClient(backend));

    const scanId = action.scanId;
    const arrayBuffer = action.arrayBuffer;
    const filename = action.filename;
    const contouringAction = action.contouringAction;
    const sliceCountTotal = action.sliceCountTotal;

    const hasThisUploadAlreadyFailed: boolean = yield select(selectors.getHasUploadFailed(action.scanId));
    if (!hasThisUploadAlreadyFailed) {
        // anonymize the scan before uploading in demo environment
        const ab2 = isDemo() ? anonymizeSlice(arrayBuffer, action.dicomMapAnonReal) : arrayBuffer;

        const blob = new Blob([ab2], { type: "" });
        try {
            yield call([client, 'sendImageFile'], blob, filename, contouringAction, sliceCountTotal);
            yield put({ type: Store.receiveFileUploadedType, filename: filename, scanId: scanId });
        }
        catch (error) {
            yield put({ type: Store.receiveUploadErrorType, error: error || "Invalid DICOM file", scanId: scanId });
        }
    }
}


function* initializeAuth() {
    // set default auths
    let firstAuthApp: string | null = null;
    for (const authApp of defaultAuths) {
        if (!firstAuthApp) { firstAuthApp = authApp; }
        yield put({ type: Store.addAuthState, authState: new AppAuthState(authApp, true) });
    }

    // add in all the optional auths -- they're logged into later as needed
    for (const authApp of getOptionalAuths()) {
        yield put({ type: Store.addAuthState, authState: new AppAuthState(authApp, false) });
    }

    // log in the first auth app
    if (firstAuthApp) {
        const firstAuth: AppAuthState = yield select(selectors.getAuthState(firstAuthApp));
        if (!firstAuth) { throw new Error(`Could not retrieve auth app ${firstAuthApp} from store!`); }
        yield put({ type: Store.startLogin, appAuthName: firstAuth.appAuthName });
        const appAuth = getAppAuthByName(firstAuth.appAuthName);

        try {
            yield call([appAuth, 'logIn']);
        } catch (err) {
            yield put({ type: Store.setLoginError, loginError: new LoginError('message' in err && err.message === POPUPS_BLOCKED_ERROR, err.message) });
        }

        yield put({ type: Store.setAuthStateAsLoggedIn, appAuthName: firstAuth.appAuthName });

        // first authentication should also have authenticated current user with rtviewer backend
        const userDetails: { username: string, email: string, permissions: UserPermissions } = yield call([rtViewerApiClient, 'getAuthenticatedUser']);
        yield put({ type: Store.setUserDetails, ...userDetails });
    }

    // do rest of the required logins one by one
    yield authLoginLogoutOnRequest(false);

    // do we have a login error? if so, stop here
    const wasLoginSuccessful: boolean = yield select(selectors.getWasLoginSuccessful());
    if (!wasLoginSuccessful) {
        console.error('Login has failed:');
        console.log(yield select(selectors.getLoginError()));
        return;
    }

    // wait for any new login/logout requests
    yield authLoginLogoutOnRequest(true);
}

function* authLoginLogoutOnRequest(loopForever: boolean) {
    const authsNeedingLogin: AppAuthState[] = [];
    const authsNeedingLogout: AppAuthState[] = [];

    let keepLooping = true;

    do {
        // begin by checking we haven't missed any auths that need login;
        authsNeedingLogin.push(...(yield select(selectors.getAuthStatesThatNeedLogin())) as AppAuthState[]);

        // start logging into app auths one by one
        while (authsNeedingLogin.length > 0) {
            const authState = authsNeedingLogin.pop() as AppAuthState;
            yield put({ type: Store.startLogin, appAuthName: authState.appAuthName });
            const appAuth = getAppAuthByName(authState.appAuthName);

            try {
                yield call([appAuth, 'logIn']);
            } catch (err) {
                yield put({ type: Store.setLoginError, loginError: new LoginError('message' in err && err.message === POPUPS_BLOCKED_ERROR, err.message) });
            }

            yield put({ type: Store.setAuthStateAsLoggedIn, appAuthName: authState.appAuthName });
        }

        // start logging out
        while (authsNeedingLogout.length > 0) {
            const authState = authsNeedingLogout.pop() as AppAuthState;
            yield put({ type: Store.startLogout, appAuthName: authState.appAuthName });
            const appAuth = getAppAuthByName(authState.appAuthName);

            try {
                yield call([appAuth, 'logOut']);
            } catch (err) {
                yield put({ type: Store.setLoginError, loginError: new LoginError('message' in err && err.message === POPUPS_BLOCKED_ERROR, err.message) });
            }

            // a successful logout will automatically redirect the user somewhere else
        }

        // stop the loop here if this isn't supposed to be an infinite loop
        if (!loopForever) {
            break;
        }

        // wait for a new login request by waiting for an existing app auth state to be set as required
        const loginOrLogoutRequest: { appAuthName: string, type: string } = yield take([Store.setAuthStateAsRequired, Store.requestLogOut]);
        if (loginOrLogoutRequest.type === Store.setAuthStateAsRequired) {
            const appAuthState: AppAuthState = yield select(selectors.getAuthState(loginOrLogoutRequest.appAuthName));
            if (appAuthState.logInProcessState === LogInProcessState.NotLoggedIn) {
                authsNeedingLogin.push(appAuthState);
            }
        } else if (loginOrLogoutRequest.type === Store.requestLogOut) {
            // log out of the (first) default app auth -- we only ever allow one user to log in at once so that's enough
            // to log out of everything
            const appAuthState: AppAuthState = yield select(selectors.getAuthState(defaultAuths[0]));
            authsNeedingLogout.push(appAuthState);
        }
    } while (keepLooping);
}

function* waitForLoginsToFinish() {
    let isWaitingForLoginsToFinish: boolean = yield select(selectors.getAuthStatesAreLoginsNeeded());
    while (isWaitingForLoginsToFinish) {
        yield take(Store.setAuthStateAsLoggedIn);
        isWaitingForLoginsToFinish = yield select(selectors.getAuthStatesAreLoginsNeeded());
    }
}

/** Perform (possibly repeatable) actions here that are needed to fully initialize the annotation page.
 */
function* initializeAnnotationPage() {
    let error = null;
    let errorMessage: string | undefined = undefined;
    let userStorageAccess: UserStorageAccountAccess[] | undefined = undefined;

    // get user access data (e.g. which annotation items user has access to)
    try {
        const userAccessJson: any = yield call([rtViewerApiClient, 'getUserAccess']);
        userStorageAccess = convertUserAccessJsonToViewModels(userAccessJson);
    } catch (ex) {
        console.error(ex);
        error = ex;
        errorMessage = `Could not initialize user access${_.isError(error) ? `: ${error.message}` : '.'}`;
    }

    if (error === null && !!userStorageAccess) {
        yield put({ type: Store.setUserStorageAccess, userStorageAccess: userStorageAccess });
        yield put({ type: Store.setIsAnnotationPageReady, isAnnotationPageReady: true });

        yield postInitializationSetupForAnnotationPage();
    } else {
        yield addNotification('Could not initialize annotation page.', NotificationType.Error, errorMessage);
        yield put({ type: Store.setIsAnnotationPageReady, isAnnotationPageReady: false });

    }
}

/** Perform actions here that should be done adjacent to initializing the annotation page but
 * can be allowed to complete after the page is already otherwise ready to be used.
 */
function* postInitializationSetupForAnnotationPage() {
    let error = null;
    let errorMessage: string | undefined = undefined;

    // get dataset split categories
    try {
        const categoriesJson: any = yield call([rtViewerApiClient, 'getDatasetCategories']);
        const categories = convertDatasetCategoryJsonIntoViewModels(categoriesJson);
        yield put({ type: Store.setDatasetCategories, datasetCategories: categories });
    } catch (ex) {
        console.error(ex);
        error = ex;
        errorMessage = `Could not initialize dataset categories${_.isError(error) ? `: ${error.message}` : '.'}`;
    }

    if (error !== null) {
        yield addNotification('An error occurred while loading internal data.', NotificationType.Error, errorMessage);
    }
}

function* addNotification(message: string, notificationType: NotificationType, detailedMessage?: string) {
    const id = `notification-${notificationType.toString()}-${Date.now().toString()}`;
    yield put({ type: Store.addNotificationType, notification: new SessionNotification(id, message, notificationType, detailedMessage) });
}

function* requestDatasetLock(datasetImage: DatasetImage, dataset: Dataset, lockAction: LockAction) {
    // start a dataset lock request
    const datasetId = dataset.getDatasetId();
    const seriesId = datasetImage.seriesId;
    yield put({ type: Store.startDatasetLockRequest, datasetId: datasetId, seriesId: seriesId, lockAction: lockAction });

    // try to get a scan lock to the specific scan in the dataset
    let mutexId: string | null = null;
    let locks: ScanLocks | null = null;
    try {
        const user: User = yield select(selectors.getUser());
        const username = user.username;

        mutexId = yield call([datasetFiles, 'enterCSVMutex'], dataset);
        locks = yield call([datasetFiles, 'setDatasetLock'], dataset, seriesId, username, lockAction);
    }
    catch (error) {
        // current error handling is to just silently remove the scan lock request
        console.log(error);
        yield put({ type: Store.undoDatasetLockRequest, datasetId: datasetId, seriesId: seriesId });
    }
    finally {
        // remove csv mutex lock if it was taken
        if (mutexId !== null) { yield call([datasetFiles, 'leaveCSVMutex'], dataset, mutexId); }
    }

    // if successful, finish the request
    if (locks !== null) {
        yield put({ type: Store.setDatasetLock, datasetId: datasetId, scanLocks: locks, clearRequests: true });
    }
}

function* loadDataset(azureShare: AzureShareInfo, reloadMetaFiles: boolean, datasetId: string) {

    if (!datasetId) {
        throw new Error(`Invalid Dataset ID: must not be falsy ('${datasetId}').`);
    }

    // mark download as started
    yield put({ type: Store.startDatasetDownload, datasetId: datasetId });

    // download the dataset
    try {
        // add or replace dataset in store, mark download as finished
        const downloadInfoFile = function* () {
            const dataset: Dataset = yield call(downloadDatasetInfo, azureShare, reloadMetaFiles, datasetId);
            yield put({ type: Store.addDataset, dataset: dataset, overrideGradings: reloadMetaFiles });
        }

        // download and replace current dataset locks info from latest from azure
        const downloadLockFile = function* () {
            const datasetLocks: ScanLocks = yield call(downloadDatasetLocks, azureShare);
            yield put({ type: Store.setDatasetLock, datasetId: datasetId, scanLocks: datasetLocks, clearRequests: true });
        }

        yield all([call(downloadInfoFile), call(downloadLockFile)]);
    }
    catch (err) {
        console.error(err);
        throw err;
    }
    finally {
        yield put({ type: Store.finishDatasetDownload, datasetId: datasetId });
    }
}

function* downloadImageAndStructureSet(datasetImage: DatasetImage, dataset: Dataset, reloadDataset: boolean = false) {
    const datasetId = dataset.getDatasetId();
    let updatedDataset: Dataset | undefined = dataset;

    // first, reload the dataset if so requested
    if (reloadDataset) {
        // just always reload meta files, the performance hit is not significant
        const reloadMetafiles = true;
        // const existingDataset: Dataset | undefined = yield select(getDataset(datasetId));
        // const reloadMetafiles = existingDataset === undefined;

        yield loadDataset(dataset.datasetFile.getShare(), reloadMetafiles, datasetId);

        updatedDataset = yield select(selectors.getDataset(datasetId));
        if (updatedDataset === undefined) {
            throw new Error(`Was not able to properly reload dataset ${datasetId}`);
        }
    }

    const downloadKey = datasetImage.downloadKey;
    const shareInfo = updatedDataset.datasetFile.getShare();
    const azureFileClient = new AzureFileClient(shareInfo);

    try {
        // step 1 -- initialize downloads of image slices
        const imageFiles: AzureFileInfo[] = yield call(() => azureFileClient.listFiles(shareInfo, datasetImage.path));
        for (const imageFile of imageFiles) {
            const fileInfo = shareInfo.getFile(datasetImage.path, imageFile.filename);
            try {
                const lower = imageFile.filename.toLowerCase();
                if (lower.endsWith(".dcm") || lower.endsWith(".gz")) {
                    const request = new AzureFileRequest(fileInfo, downloadKey);
                    yield put({ type: Store.receiveDownloadAddFile, request: request });
                }
            }
            catch (error) {
                console.error(`Error when trying to download image file ${fileInfo.toString()}`);
                console.log(error);
                yield put({ type: Store.receiveDownloadErrorType, error: new AzureDownloadError(fileInfo, downloadKey, error) });
            }
        }

        // step 2 -- initialize downloads of structure sets
        for (let s = 0; s < datasetImage.structureSets.length; ++s) {
            const structureSet = datasetImage.structureSets[s];

            // get a "best guess file" that's about in the correct place
            const bestGuessFile = datasetFiles.getStructureSetFileInfo(shareInfo, datasetImage.patientId, datasetImage.frameOfReferenceUid,
                structureSet.seriesId, structureSet.sopId);

            // now get the actual file(s) from the same folder
            const structureSetFiles: AzureFileInfo[] = yield call(() => azureFileClient.listFiles(bestGuessFile.getPath()));
            for (const structureSetFile of structureSetFiles) {
                try {
                    const lower = structureSetFile.filename.toLowerCase();
                    if (lower.endsWith(".gz") || lower.endsWith(".dcm")) {
                        const request = new AzureFileRequest(structureSetFile, downloadKey);
                        yield put({ type: Store.receiveDownloadAddFile, request: request });
                    }
                }
                catch (error) {
                    console.error(`Error when trying to download structure set file ${structureSetFile.toString()}`);
                    console.log(error);
                    yield put({ type: Store.receiveDownloadErrorType, error: new AzureDownloadError(structureSetFile, downloadKey, error) });
                }
            }

        }
    }
    catch (error) {
        console.error(`Error when trying to download image and structure set files from ${shareInfo.toString()} for download key "${downloadKey}"`);
        console.log(error);
        yield put({ type: Store.receiveDownloadErrorType, error: new AzureDownloadError(shareInfo, downloadKey, error) });
    }
}

/**
 * Download annotation items specified in the given annotation query object.
 */
function* downloadAnnotationQueryItems(annotationQuery: AnnotationQuery) {
    let datasetId: string | undefined;
    let dataset: Dataset | undefined;
    let datasetImage: DatasetImage | null = null;

    // load up dataset & scan if they've been defined
    if (annotationQuery.hasWork()) {
        try {
            // 0. Check that user has access to the item defined in the query

            // 0.1 wait for annotation page to finish initializing
            let userAccess: UserStorageAccountAccess[] | undefined = yield select(selectors.getUserAccess());
            if (userAccess === undefined) {
                const { interval, setUserAccess }: { interval: unknown, setUserAccess: { userStorageAccess: UserStorageAccountAccess[] } } = yield race({
                    interval: delay(30 * 1000), // 30 second timeout in case getting user access data from backend has some hiccups
                    setUserAccess: take(Store.setUserStorageAccess),
                });
                if (setUserAccess) {
                    userAccess = setUserAccess.userStorageAccess;
                } else {
                    throw new Error('Timed out while waiting to get user access permissions.');
                }
            }

            // 0.2 ensure this user is allowed to view current annotation item
            let isAccessAllowed = true;
            let accessNotAllowedError: string | undefined = undefined;
            const storageAccountAccess = userAccess.find(ua => ua.name === annotationQuery.storageAccount);
            if (!storageAccountAccess) {
                accessNotAllowedError = `storage account "${annotationQuery.storageAccount}"`;
                isAccessAllowed = false;
            } else {
                if (storageAccountAccess.fileShares !== null) {
                    const fileShareAccess = storageAccountAccess.fileShares.find(fs => fs.name === annotationQuery.fileShare);
                    if (!fileShareAccess) {
                        accessNotAllowedError = `file share "${annotationQuery.fileShare}"`;
                        isAccessAllowed = false;
                    } else {
                        if (fileShareAccess.patientIds !== null) {
                            const patientIdAccess = fileShareAccess.patientIds.find(pId => pId.name === annotationQuery.patientName);
                            if (!patientIdAccess) {
                                accessNotAllowedError = `patient "${annotationQuery.patientName}"`;
                                isAccessAllowed = false;
                            } else {
                                if (patientIdAccess.seriesUids !== null && !patientIdAccess.seriesUids.includes(annotationQuery.imageSeriesInstanceUid!)) {
                                    accessNotAllowedError = `image "${annotationQuery.imageSeriesInstanceUid}"`;
                                    isAccessAllowed = false;
                                }
                            }
                        }
                    }
                }
            }

            if (!isAccessAllowed) {
                throw new Error(`You do not have access to this item (${accessNotAllowedError}). Please contact an admin if you think this is in error.`);
            }

            // 1. Load the dataset
            const azureShare = new AzureShareInfo(annotationQuery.storageAccount, annotationQuery.fileShare!);
            datasetId = Dataset.generateDatasetId(azureShare);
            yield loadDataset(azureShare, true, datasetId);

            // 2. Load image and structure sets
            dataset = yield select(selectors.getDataset(datasetId));
            if (!dataset) {
                throw new Error(`Unable to load dataset ${datasetId}`);
            }
            datasetImage = dataset.getImage(annotationQuery.imageSeriesInstanceUid!);
            if (!datasetImage) {
                throw new Error(`Did not find image "${annotationQuery.imageSeriesInstanceUid!}" in dataset ${datasetId}`);
            }
            yield downloadImageAndStructureSet(datasetImage, dataset, false);
        }
        catch (err) {
            // Set current work state into an error state. Also display a notification message.
            const errorTitle = 'An error occurred when trying to load dataset or image';
            const errorMessage = err.message || undefined;
            console.log(`${errorTitle}:`)
            console.error(err);
            yield put({ type: Store.setCurrentWorkState, workState: new WorkState(null, null, false, false, null, errorMessage) });
            yield addNotification(`${errorTitle}.`, NotificationType.Error, errorMessage);
            throw err;
        }

        // 3. Set the current workstate
        const user: User = yield select(selectors.getUser());
        const locks: ScanLocks = yield select(selectors.getLocksForDataset(datasetId));
        const canEdit = datasetFiles.getUserCanEdit(user, locks, datasetImage.seriesId);
        const canCreateRtstruct = datasetFiles.getUserCanCreateRtstruct(user, locks, datasetImage.seriesId);
        yield put({ type: Store.setCurrentWorkState, workState: new WorkState(datasetImage, dataset, canEdit, canCreateRtstruct, annotationQuery.defaultStructureSetUid || null) });
    }
    else if (annotationQuery.hasDatasetIndex()) {
        // Immediately set current work state to a dataset index work state to avoid unnecessarily showing a splash screen when we don't want it
        yield put({ type: Store.setCurrentWorkState, workState: new WorkState(annotationQuery.storageAccount, annotationQuery.fileShare || null, null) });

        // Load the dataset if fileshare has been defined (otherwise we just load the available file shares on the annotation page/dataset browser component)
        if (annotationQuery.fileShare !== undefined) {
            const azureShare = new AzureShareInfo(annotationQuery.storageAccount, annotationQuery.fileShare);
            datasetId = Dataset.generateDatasetId(azureShare);
            yield loadDataset(azureShare, true, datasetId);
            dataset = yield select(selectors.getDataset(datasetId));
            yield put({ type: Store.setCurrentWorkState, workState: new WorkState(annotationQuery.storageAccount, annotationQuery.fileShare || null, dataset || null) });
        }

    } else {
        throw new Error('Invalid annotation query item');
    }

}

/**
 * Save grading sheet of a single structure set into a file into azure.
 */
function* saveStructureSetGradingToFile(structureSetId: string, ssGrading: StructureSetGrading | null, dataset: Dataset) {
    const gradingToSave: datasetFiles.GradingToSave = { ssId: structureSetId, ssGrading };
    yield startSavingDatasetGradingsToFile([gradingToSave], dataset);
}

/**
 * Start saving dataset gradings into a physical file (i.e. azure). In practice this function will
 * first apply local modifications and then call the rest of the save functionality through a
 * debounced store action.
 * @param gradingsToSave Array of latest modifications to gradings.
 * @param dataset Dataset to apply these modifications for.
 */
function* startSavingDatasetGradingsToFile(gradingsToSave: datasetFiles.GradingToSave[], dataset: Dataset) {
    // 1. Signal that we're starting a save operation & keep track of what we've modified
    yield put({ type: Store.startDatasetGradingSave, gradingsToSave, dataset });

    // 2. Immediate state update -- set new gradings into store until we've synced the real state from azure
    yield put({ type: Store.setStructureSetGrading, gradingsToSave, dataset });

    // 3. Do the actual save operation, but with a debounce so we don't spam it unnecessarily
    yield put({ type: Store.debouncedFinishDatasetGradingSave, gradingsToSave, dataset });
}

/**
 * Finish saving dataset gradings to azure disk. The actual save operation to cloud is performed 
 * here.
 * @param action Redux action object. Must contain a type (for redux/saga to work) and a
 * reference to the dataset we're operating on.
 */
function* finishSavingDatasetGradingsToFile(action: { type: any, dataset: Dataset }) {
    const { dataset } = action;
    let mutexId: string | null = null;
    let wasSuccessful = false;
    let gradingsToSave: GradingToSave[] = [];
    try {
        const user: User = yield select(selectors.getUser());
        mutexId = yield call(datasetFiles.enterCSVMutex, dataset);

        // get all current local modifications to gradings at once
        gradingsToSave = yield select(selectors.getModifiedDatasetGradings(dataset.getDatasetId()));

        // possibly nothing to save here
        if (gradingsToSave.length !== 0) {
            // save the new gradings to the file share and return the latest dataset grading file with our new modifications in it
            const syncedNewGradings: DatasetGradings = yield call(datasetFiles.saveGradings, dataset, gradingsToSave, user.username);

            // create a backup of the new dataset gradings we just created
            yield call(backupGradings, syncedNewGradings, dataset.datasetFile.getShare(), user.username, gradingsToSave);
        }

        wasSuccessful = true;

        // Right here we could then put the new, synced dataset gradings to store by calling yield put for Store.setDatasetGradings.
        // However, doing so can cause weird interactions in UI due to the asynchronous nature of these saving operations
        // and we'd need to do reconciliation between incoming and local, potentially at this point many times modified data.
        // So instead we're ignoring the up-to-date state of the returned dataset grading sheet.
        //
        // This has following repercussions:
        //
        // 1. We will now use local grading sheet data for the current scan as its single source of truth.
        //    This is ok as a) we keep track of all local changes, b) user needs a lock to edit grading sheets
        //    so no one else can modify the gradings of the same scan simultaneously, c) all changes are
        //    eventually saved to the dataset gradings file.
        //
        // 2. The local copy of dataset gradings is no now longer in sync with the data in the file share. This is ok
        //    as a) changing to a different scan will cause dataset gradings to get refreshed, b) we never save
        //    the entire dataset grading file using local cached data, the current up-to-date dataset gradings
        //    data is always fetched first from the file share, it just doesn't get updated to the redux store.
        //
        // TODO: in future we may want to consider doing reconciliation here instead of having to reload all gradings
        // on all possible context changes.
    }
    catch (error) {
        wasSuccessful = false;
        console.log(error);
        alert("Error saving gradings. See console for details (F12)");
    }
    finally {
        if (mutexId !== null) { yield call(datasetFiles.leaveCSVMutex, dataset, mutexId); }
        yield put({ type: Store.finishDatasetGradingSave, wasSuccessful, dataset, gradingsToSave });
    }
}

/**
 * Reloads and replaces the entire dataset gradings for the specified dataset.
 */
function* reloadDatasetGradings(dataset: Dataset) {
    const gradings: DatasetGradings = yield call(downloadGradings, dataset.datasetFile.getShare());
    yield put({ type: Store.setDatasetGradings, datasetId: dataset.getDatasetId(), datasetGradings: gradings });
}

/** Saves a scan from memory to local disk. */
function* saveScanToUserDevice(scanId: string, zipFilename: string, anonymizeScan: boolean) {
    const scan = yield select(selectors.getScan(scanId));
    const anonymizationMap = new DicomMapAnonReal();
    if (scan !== undefined && scan.slices !== undefined) {
        let scanZip = new JSZip();
        const sopInstanceUIDs = Object.keys(scan.slices);
        let scanNumber = 0;
        for (const sopInstanceUID of sopInstanceUIDs) {
            const slice = scan.slices[sopInstanceUID];
            if (_.isArrayBuffer(slice.arrayBuffer) && _.isString(slice.filename)) {
                const useGzip = (slice.filename as string).endsWith('.dcm.gz');
                let usedFilename = anonymizeScan ? `anon${lpad(scanNumber, 3)}.${useGzip ? 'dcm.gz' : 'dcm'}` : slice.filename;
                let usedFiledata = anonymizeScan ? anonymizeSlice(slice.arrayBuffer, anonymizationMap) : slice.arrayBuffer;
                scanZip.file(usedFilename, useGzip ? compressGZip(usedFiledata) : usedFiledata);
            }
            scanNumber++;
        }

        if (Object.keys(scanZip.files).length > 0) {
            const resultFile = yield call([scanZip, 'generateAsync'], { type: 'blob' });
            saveFileOnUserDevice(zipFilename, resultFile);
        }
    }
}

function* watchLiveReviewAutoLoad() {
    // first, wait for confirmation that we're in a live review page
    const liveReviewQueries: { queryV1: LiveReviewQueryV1, queryV2: LiveReviewQueryV2 } = yield take(Store.setLiveReviewQueries);
    const { queryV1, queryV2 } = liveReviewQueries;
    if (!queryV1 && !queryV2) {
        // no live review queries, nothing to do
        return;
    }

    // if an app auth is not yet set as required, make it so
    if (queryV2) {
        try {
            const requiredBackend = getRequiredLiveReviewBackend(queryV2);
            const appAuth = getAppAuthByTier(requiredBackend.tier);
            if (!appAuth.isLoggedIn) {
                // mark this auth as being required -- note that it might already be required, or might already be in the process of being logged in
                yield put({ type: Store.setAuthStateAsRequired, appAuthName: appAuth.appName });
            }
        }
        catch (error) {
            const errorMessage = (error as Error).message;
            console.error(errorMessage);
            yield put({ type: Store.setLiveReviewErrorMessage, errorMessage: errorMessage });
            throw error;
        }
    }

    // wait for the possible new login to finish
    yield waitForLoginsToFinish();

    // load livereview files
    const isLiveReviewDownloadStarted: boolean = yield select(selectors.isLiveReviewDownloadInitialized());
    if (!isLiveReviewDownloadStarted) {
        // prioritize v1 query (direct azure fileshare access), if not available then try v2 query (backend api file download)
        try {
            if (queryV1) {
                // v1 queries don't need access to contouring backend, so backend client can be set to null
                const liveReviewBackendClient = null;
                const liveReviewClient = new LiveReviewClient(liveReviewBackendClient);

                yield put({ type: setLiveReviewIsDownloading, isDownloadingLiveReviewFiles: true });
                const requests: LiveReviewV1FileRequest[] = yield call([liveReviewClient, 'getLiveReviewFilesV1'], queryV1);
                for (const request of requests) {
                    request.downloadKey = Store.liveReviewDownloadKey;
                    yield put({ type: Store.receiveDownloadAddFile, request: request });
                }
            }
            else if (queryV2) {
                const liveReviewBackendClient = getRequiredLiveReviewBackendClient(queryV2);
                const liveReviewClient = new LiveReviewClient(liveReviewBackendClient);
                yield put({ type: setLiveReviewIsDownloading, isDownloadingLiveReviewFiles: true });
                const paths: string[] = yield call([liveReviewClient, 'getLiveReviewFilesV2Paths'], queryV2);
                for (const path of paths) {
                    const request = new LiveReviewV2FileRequest(path, Store.liveReviewDownloadKey, queryV2);
                    yield put({ type: Store.receiveDownloadAddFile, request: request });
                }
            }
        }
        catch (error) {
            console.error(error);
            yield put({ type: Store.receiveDownloadErrorType, error: new ApiDownloadError('', Store.liveReviewDownloadKey, error) });
        }
    }
}

function* watchDatasetLockRequests() {
    while (true) {
        const action: { datasetImage: DatasetImage, dataset: Dataset, lockAction: LockAction } = yield take(Store.requestDatasetLock);
        yield spawn(requestDatasetLock, action.datasetImage, action.dataset, action.lockAction);
    }
}

function* watchGradingSaveOperations() {

    // start two different loops that spawn calls that both eventually call saveDatasetGradingsToFile

    const watchSaveStructureSetGradingRequests = function* () {
        while (true) {
            const action: { structureSetId: string, ssGrading: StructureSetGrading | null, dataset: Dataset } = yield take(Store.saveStructureSetGrading);
            yield spawn(saveStructureSetGradingToFile, action.structureSetId, action.ssGrading, action.dataset);
        }
    }

    const watchSaveDatasetGradingsRequests = function* () {
        while (true) {
            const action: { gradingsToSave: datasetFiles.GradingToSave[], dataset: Dataset } = yield take(Store.saveDatasetGradings);
            yield spawn(startSavingDatasetGradingsToFile, action.gradingsToSave, action.dataset);
        }
    }

    // debounce calling the actual gradings file azure disk save operation as that's a relatively slow operation
    const finishDebouncedDatasetGradingSave = function* () {
        yield debounce(DEBOUNCE_GRADINGS_SAVE_MS, Store.debouncedFinishDatasetGradingSave, finishSavingDatasetGradingsToFile);
    }


    yield all([call(watchSaveStructureSetGradingRequests), call(watchSaveDatasetGradingsRequests), call(finishDebouncedDatasetGradingSave)]);


}

function* watchReloadDatasetGradings() {
    while (true) {
        const action: { dataset: Dataset } = yield take(Store.reloadDatasetGradings);
        yield spawn(reloadDatasetGradings, action.dataset);
    }
}

function* watchDatasetDownloads() {
    while (true) {
        // get what dataset we're trying to download
        const action: { azureShare: AzureShareInfo, reloadMetaFiles: boolean, datasetId: string } = yield take(Store.requestDownloadDataset);
        yield spawn(loadDataset, action.azureShare, action.reloadMetaFiles, action.datasetId);
    }
}

function* watchImageAndStructureSetDownloads() {
    while (true) {
        const action: { datasetImage: DatasetImage, dataset: Dataset, reloadDataset: boolean | undefined } = yield take(Store.requestImageAndStructureSetDownload);
        yield spawn(downloadImageAndStructureSet, action.datasetImage, action.dataset, action.reloadDataset);
    }
}

function* watchAnnotationQueries() {
    while (true) {
        const action: { annotationQuery: AnnotationQuery } = yield take(Store.loadAnnotationQuery);
        yield spawn(downloadAnnotationQueryItems, action.annotationQuery);
    }
}

function* watchSaveScanToUserDevice() {
    while (true) {
        const action: { scanId: string, zipFilename: string, anonymizeScan: boolean } = yield take(Store.requestSaveScanToUserDisk);
        yield spawn(saveScanToUserDevice, action.scanId, action.zipFilename, action.anonymizeScan);
    }
}

function* watchInitializeAnnotationPage() {
    while (true) {
        yield take(Store.initializeAnnotationPage);
        yield spawn(initializeAnnotationPage);
    }
}

function* watchDicomDownloads() {
    // @ts-ignore: the return object from the yield call function is a complex redux saga object
    const queue = yield call(channel);

    // create 5 'worker threads'
    for (let i = 0; i < 5; i++) {
        yield fork(handleDownload, queue);
    }
    while (true) {
        // @ts-ignore: TODO: strongly type expected action payload from this yield take call
        const action = yield take(Store.receiveDownloadAddFile);
        yield put(queue, action);
    }
}

function* handleDownload(chan: any) {
    while (true) {
        // @ts-ignore: the return object from the yield take function is a complex redux saga object
        const action = yield take(chan);
        if (action.request && action.request instanceof LiveReviewV2FileRequest) {
            yield downloadLiveReviewV2DicomFromMVBackendAsync(action);
        }
        else {
            yield downloadDicomAsync(action);
        }
    }
}

function* watchDicomUploads() {
    // @ts-ignore: the return object from the yield call function is a complex redux saga object
    const queue = yield call(channel);

    // create 5 'worker threads'
    const workerThreadCount = getAreConcurrentUploadsEnabled() ? 5 : 1;
    for (let i = 0; i < workerThreadCount; i++) {
        yield fork(handleUpload, queue);
    }
    while (true) {
        // @ts-ignore: TODO: strongly type expected action payload from this yield take call
        const action = yield take(Store.receiveUploadAddFile);
        yield put(queue, action);
    }
}

function* handleUpload(chan: any) {
    while (true) {
        // @ts-ignore: the return object from the yield take function is a complex redux saga object
        const action = yield take(chan);
        yield uploadDicomAsync(action);
    }
}

export default function* rootSaga() {
    // don't actually run root saga until a component tells to initialize the full application
    yield take(Store.initializeApp);

    yield all([
        initializeAuth(),
        watchInitializeAnnotationPage(),
        watchLiveReviewAutoLoad(),
        watchDatasetDownloads(),
        watchDatasetLockRequests(),
        watchGradingSaveOperations(),
        watchReloadDatasetGradings(),
        watchImageAndStructureSetDownloads(),
        watchAnnotationQueries(),
        watchDicomDownloads(),
        watchDicomUploads(),
        watchBatchJobs(),
        watchBatchJobRequests(),
        watchStructureTemplates(),
        watchVersion(),
        watchUserUpdate(),
        watchCurrentDatasetLock(),
        watchSaveScanToUserDevice(),
    ])
}
